import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { TIER_SHORT, tierDesc } from "../lib/forecast/engines.js";
import { ui } from "../lib/telemetry/instrument.js";
import { IconCheck, IconChevronDown, IconLayers } from "./ui/Icons.jsx";

/** The chat composer's depth picker (the default `ids`). */
const CHAT_TIER_IDS = {
  toggle: ui("chat.tier-toggle"),
  menu: ui("chat.tier-menu"),
  option: ui("chat.tier-option"),
};

/**
 * The depth selector ("Depth: Quick ▾") used by the chat composer and the Ask hero. Renders the
 * current depth plus a caret; clicking opens a menu of the available depths. Pure props — no
 * store reads — so the caller owns the selection and this component owns only whether the menu
 * is open.
 *
 * TWO MENU PLACEMENTS, one component:
 *  • ANCHORED (default) — a popup that opens upward from the trigger.
 *  • OVERLAY (`standalone` + `overlayRef`) — the open menu is portalled INTO that element and
 *    stretched over it, so the menu lines up with the composer box by construction.
 *
 * @param {object} props
 * @param {string[]} props.tiers - Tier keys to offer, in display order (e.g. ["quick","deep"]).
 * @param {string|null} props.current - The selected tier key, or null when the active engine
 *   carries no tier tag (the trigger then shows the first offered tier as a placeholder).
 * @param {(tier: string) => void} props.onSelect - Called with the chosen tier key.
 * @param {object} props.C - Theme color tokens.
 * @param {boolean} [props.standalone=false] - Allow the overlay placement.
 * @param {{current: HTMLElement|null}|null} [props.overlayRef=null] - Element to overlay with
 *   the open menu (standalone only).
 * @param {"start"|"end"} [props.align="end"] - Which end of its row the trigger sits at.
 * @param {"above"|"below"} [props.placement="above"] - Which way the anchored menu opens.
 * @param {{toggle: object, menu: object, option: object}} [props.ids=CHAT_TIER_IDS] - The
 *   `data-ui` of its toggle, menu and options, named for the picker's use (the composer's, the
 *   ask box's, a typed trigger's). Each caller builds its set with literal `ui()` calls, so the
 *   telemetry scan sees every id.
 * @param {"composer"|"inset"} [props.variant="composer"] - The trigger: "Depth: Quick ▾", or a
 *   small "Quick ▾" inside a `sy-btn-split` box, after the button whose depth it sets.
 * @param {string} [props.heading="Depth"] - The line above the options.
 * @param {string} [props.title] - The trigger's tooltip.
 * @param {boolean} [props.disabled=false] - The trigger cannot be pressed and the menu stays shut.
 */
export default function TierSelector({
  tiers,
  current,
  onSelect,
  C,
  standalone = false,
  overlayRef = null,
  align = "end",
  placement = "above",
  ids = CHAT_TIER_IDS,
  variant = "composer",
  heading = "Depth",
  title = "Choose how deep Sayer researches",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  // The menu animates BOTH ways, so a close can't just unmount: `closing` keeps it mounted
  // playing the exit while the timer runs out. Every close path goes through closeMenu.
  const [closing, setClosing] = useState(false);
  const closeTimer = useRef(null);
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  // The panel may be PORTALLED out of this root, so outside-click tests it separately.
  const panelRef = useRef(null);
  const MENU_ANIM_MS = 200;
  const closeMenu = useCallback(() => {
    if (!open) return;
    setClosing(true);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, MENU_ANIM_MS);
  }, [open]);
  const openMenu = () => {
    clearTimeout(closeTimer.current);
    setClosing(false);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      closeMenu();
    };
    // telemetry-gesture: an outside click closes the depth menu (dismissing)
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, closeMenu]);

  // Escape closes and returns focus to the trigger.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      closeMenu();
      btnRef.current?.focus();
    };
    // telemetry-gesture: Escape closes the depth menu (dismissing)
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, closeMenu]);

  const itemRefs = useRef([]);
  const focusIdx = useRef(0);

  // Opening moves focus INTO the menu. Deps are `[open]` ALONE, deliberately: `tiers` is
  // rebuilt by the parent on every render, and re-running would snap focus mid-navigation.
  useEffect(() => {
    if (!open) return;
    const at = tiers.indexOf(current);
    focusIdx.current = at < 0 ? 0 : at;
    itemRefs.current[focusIdx.current]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /** Arrow/Home/End move focus between items; both ends wrap. */
  const onMenuKey = (e) => {
    const last = tiers.length - 1;
    const at = focusIdx.current;
    let next;
    if (e.key === "ArrowDown") next = at >= last ? 0 : at + 1;
    else if (e.key === "ArrowUp") next = at <= 0 ? last : at - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = last;
    else return;
    e.preventDefault();
    e.stopPropagation();
    focusIdx.current = next;
    itemRefs.current[next]?.focus();
  };

  const overlay = standalone && !!overlayRef?.current;
  const label = TIER_SHORT[current] || TIER_SHORT[tiers[0]] || "";
  const choose = (t) => {
    closeMenu();
    btnRef.current?.focus();
    if (onSelect) onSelect(t);
  };

  const menuPanel = (
    <div
      {...ui(ids.menu["data-ui"])}
      ref={panelRef}
      role="menu"
      aria-label="Depth"
      onKeyDown={onMenuKey}
      style={{
        position: "absolute",
        ...(overlay
          ? { inset: -1, boxSizing: "border-box", justifyContent: "center", overflowY: "auto" }
          : {
              ...(placement === "below"
                ? { top: "100%", marginTop: 6 }
                : { bottom: "100%", marginBottom: 6 }),
              ...(align === "start" ? { left: 0 } : { right: 0 }),
              width: 280,
              maxWidth: "80vw",
            }),
        background: C.tooltipBg || C.panel,
        border: `1px solid ${C.border}`,
        borderRadius: overlay ? 8 : 10,
        padding: 6,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        zIndex: 20,
        boxShadow: C.shadowBox,
        transformOrigin: "bottom center",
        animation: `${closing ? "tierMenuOut" : "tierMenuIn"} ${MENU_ANIM_MS}ms cubic-bezier(0.65, 0, 0.35, 1) forwards`,
        pointerEvents: closing ? "none" : "auto",
      }}
    >
      <style>{`
    @keyframes tierMenuIn {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes tierMenuOut {
      from { opacity: 1; transform: translateY(0); }
      to   { opacity: 0; transform: translateY(4px); }
    }
    .tier-item:hover, .tier-item:focus-visible { background: var(--c-rowActive); outline: none; }
  `}</style>
      <div style={{ fontSize: 12, color: C.gray, padding: "4px 8px 6px" }}>{heading}</div>
      {tiers.map((t, i) => {
        const on = current === t;
        return (
          <button
            {...ui(ids.option["data-ui"], t)}
            key={t}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
            type="button"
            role="menuitemradio"
            aria-checked={on}
            className="tier-item"
            // -1, not 0: focus moves between items by arrow key, so Tab leaves the menu.
            tabIndex={-1}
            onClick={() => choose(t)}
            style={{
              appearance: "none",
              background: "none",
              border: "none",
              borderRadius: 6,
              padding: "8px 8px",
              textAlign: "left",
              cursor: "pointer",
              color: C.white,
              display: "grid",
              gridTemplateColumns: "16px 1fr",
              columnGap: 8,
              alignItems: "start",
              font: "inherit",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                color: C.accent,
                display: "flex",
                paddingTop: 1,
                visibility: on ? "visible" : "hidden",
              }}
            >
              <IconCheck width={15} height={15} />
            </span>
            <span style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{TIER_SHORT[t]}</span>
              {tierDesc(t) && (
                <span style={{ fontSize: 12, color: C.gray, lineHeight: 1.4 }}>{tierDesc(t)}</span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );

  const inset = variant === "inset";
  // A trigger disabled while its menu is open leaves the menu shut.
  const shown = open && !disabled;
  const chevron = (
    <IconChevronDown
      style={{
        width: 12,
        height: 12,
        transform: shown ? "rotate(180deg)" : "none",
        transition: "transform 0.15s",
      }}
    />
  );

  return (
    <div
      ref={rootRef}
      style={{
        position: "relative",
        display: "flex",
        ...(inset ? { alignSelf: "stretch" } : null),
        ...(standalone ? { width: "100%" } : null),
        justifyContent: align === "start" ? "flex-start" : "flex-end",
      }}
    >
      <button
        {...ui(ids.toggle["data-ui"])}
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={shown}
        aria-label={`Depth: ${label}`}
        title={title}
        disabled={disabled}
        onClick={() => (open && !closing ? closeMenu() : openMenu())}
        className={inset ? "sy-btn-split__toggle" : "sy-btn sy-btn--ghost sy-btn--sm"}
        style={inset ? undefined : { gap: 6, paddingLeft: 8, paddingRight: 8 }}
      >
        {inset ? (
          <span className="sy-btn-split__chip">
            {label}
            {chevron}
          </span>
        ) : (
          <>
            <IconLayers />
            <span style={{ color: C.gray }}>Depth:</span>
            <span style={{ color: C.white, fontWeight: 500 }}>{label}</span>
            {chevron}
          </>
        )}
      </button>
      {/* telemetry-unsurfaced: the overlay placement portals the menu into the caller's element,
          so it stays inside that element's surface. */}
      {shown && (overlay ? createPortal(menuPanel, overlayRef.current) : menuPanel)}
    </div>
  );
}
